/** @jsxImportSource @emotion/react */
import { useState, useRef, useEffect } from "react";

import { Avatar } from "primereact/avatar";
import { OverlayPanel } from "primereact/overlaypanel";
import { Divider } from "primereact/divider";
import { Skeleton } from "primereact/skeleton";
import { Toast } from "primereact/toast";

import { accessAPI, readFromLS, logout, storeInLS } from "../../utils";
import Version from "../version";

/**
 * Componente unificado de menú de usuario con opciones configurables.
 *
 * @component
 * @param {Object} props - Propiedades del componente
 * @param {boolean} [props.mostrarAvatar=true] - Muestra el avatar del usuario
 * @param {boolean} [props.fetchInterno=true] - Si true, obtiene infoAccesos internamente. Si false, usa el prop infoAccesos
 * @param {Object} [props.infoAccesos] - Información de accesos del usuario (requerido si fetchInterno=false)
 * @param {Array<string>} [props.opcionesRol=['administrador', 'auditor', 'cliente']] - Roles disponibles para cambiar
 * @param {boolean} [props.permitirCambioEmpresa=false] - Permite cambiar entre múltiples empresas/clientes
 * @param {boolean} [props.manejarUsuarioProvisorio=false] - Maneja el caso de usuario provisorio (oculta editar datos)
 * @param {boolean} [props.modoExpandido=false] - Renderiza el menú expandido (sin OverlayPanel), útil para sidebars móviles
 * @param {function} [props.onAccionEjecutada] - Callback que se ejecuta después de cualquier acción del menú (útil para cerrar sidebars)
 * @param {Array<{etiqueta: string, uri: string, icono?: string}>} [props.botonesCustom=[]] - Botones personalizados que se renderizan sobre el botón Salir
 */
export default function MenuUsuario({
  mostrarAvatar = true,
  fetchInterno = true,
  infoAccesos: infoAccesosProp = null,
  opcionesRol = ["administrador", "auditor", "cliente"],
  permitirCambioEmpresa = false,
  manejarUsuarioProvisorio = false,
  modoExpandido = false,
  onAccionEjecutada = null,
  botonesCustom = []
}) {
  const [modalUsuarioAbierto, setModalUsuarioAbierto] = useState(false);
  const [usuarioLogueado, setUsuarioLogueado] = useState(null);
  const [infoAccesos, setInfoAccesos] = useState(infoAccesosProp);
  const [loader, setLoader] = useState(true);
  const [cambiandoEmpresa, setCambiandoEmpresa] = useState(false);

  const overlayMenuUsuario = useRef(null);
  const botonPerfilUsuario = useRef(null);
  const toast = useRef(null);

  // Sincronizar infoAccesos cuando viene por prop
  useEffect(() => {
    if (!fetchInterno && infoAccesosProp) {
      setInfoAccesos(infoAccesosProp);
    }
  }, [fetchInterno, infoAccesosProp]);

  useEffect(() => {
    if (!modoExpandido && overlayMenuUsuario.current) {
      if (modalUsuarioAbierto) {
        overlayMenuUsuario.current.show({
          event: null,
          target: botonPerfilUsuario.current
        });
      } else {
        overlayMenuUsuario.current.hide();
      }
    }
  }, [modalUsuarioAbierto, modoExpandido]);

  useEffect(() => {
    let intentos = 0;
    const maxIntentos = 10;
    const intervalo = 300;

    const cargarUsuario = () => {
      const token = readFromLS(process.env.REACT_APP_LS_LOGIN_TOKEN);

      if (!token) {
        if (intentos < maxIntentos) {
          intentos++;
          setTimeout(cargarUsuario, intervalo);
        }
        return;
      }

      accessAPI(
        "GET",
        "usuario/me",
        null,
        (respuesta) => {
          setUsuarioLogueado(respuesta.usuario);
          setLoader(false);
        },
        (error) => {
          if ((error.status === 403 || error.status === 401) && intentos < maxIntentos) {
            intentos++;
            setTimeout(cargarUsuario, intervalo);
          } else {
            console.error("Error cargando datos del usuario:", error);
            setLoader(false);
          }
        }
      );
    };

    cargarUsuario();

    // Cargar información de accesos solo si fetchInterno es true
    if (fetchInterno) {
      accessAPI(
        "GET",
        "usuario/info-accesos",
        null,
        (respuesta) => {
          setInfoAccesos(respuesta);
        },
        (error) => {
          console.error("Error cargando información de accesos:", error);
        }
      );
    }
  }, [fetchInterno]);

  function mostrarError(mensaje) {
    toast.current?.show({
      severity: "error",
      summary: "Error",
      detail: mensaje,
      sticky: true
    });
  }

  function ejecutarConCallback(accion) {
    accion();
    if (onAccionEjecutada) {
      onAccionEjecutada();
    }
  }

  function handleLogout() {
    ejecutarConCallback(() => logout());
  }

  function handleCambiarEmpresa(clienteId) {
    if (cambiandoEmpresa) return;

    setCambiandoEmpresa(true);

    accessAPI(
      "POST",
      "usuario/cambiar-acceso",
      { clienteid: clienteId },
      (respuesta) => {
        storeInLS(process.env.REACT_APP_LS_LOGIN_TOKEN, respuesta.token);
        if (onAccionEjecutada) onAccionEjecutada();
        window.location.href = "/";
      },
      (error) => {
        mostrarError(
          "No se pudo cambiar de empresa. Por favor, intenta nuevamente."
        );
        setCambiandoEmpresa(false);
      }
    );
  }

  function handleIrAdministrador() {
    if (!infoAccesos?.esAdministrador) return;

    accessAPI(
      "POST",
      "usuario/cambiar-rol",
      { tipoAcceso: "administrador" },
      (respuesta) => {
        if (onAccionEjecutada) onAccionEjecutada();
        window.location.href = respuesta.url;
      },
      (error) => {
        mostrarError(
          "No se pudo cambiar a administrador. Por favor, intenta nuevamente."
        );
      }
    );
  }

  function handleIrAuditor() {
    if (!infoAccesos?.esAuditor) return;

    accessAPI(
      "POST",
      "usuario/cambiar-rol",
      { tipoAcceso: "auditor" },
      (respuesta) => {
        if (onAccionEjecutada) onAccionEjecutada();
        window.location.href = respuesta.url;
      },
      (error) => {
        mostrarError(
          "No se pudo cambiar a auditor. Por favor, intenta nuevamente."
        );
      }
    );
  }

  function handleIrCliente() {
    const cliente = infoAccesos?.clienteActual || infoAccesos?.clientes?.[0];

    if (!cliente) {
      return;
    }

    accessAPI(
      "POST",
      "usuario/cambiar-rol",
      { tipoAcceso: "cliente", clienteid: cliente.id },
      (respuesta) => {
        if (onAccionEjecutada) onAccionEjecutada();
        window.location.href = respuesta.url;
      },
      (error) => {
        mostrarError(
          "No se pudo cambiar a cliente. Por favor, intenta nuevamente."
        );
      }
    );
  }

  function handleBotonCustom(uri, event) {
    if (event?.metaKey || event?.ctrlKey) {
      window.open(uri, "_blank");
      return;
    }
    ejecutarConCallback(() => {
      window.location.href = uri;
    });
  }

  // Verificar si el usuario tiene acceso de cliente
  const esCliente = infoAccesos?.clientes && infoAccesos.clientes.length > 0;

  // Verificar si es usuario provisorio
  const esUsuarioProvisorio =
    manejarUsuarioProvisorio &&
    infoAccesos &&
    !infoAccesos.esAdministrador &&
    !infoAccesos.esAuditor &&
    (!infoAccesos.clientes || infoAccesos.clientes.length === 0);

  // Verificar qué opciones de rol mostrar
  const mostrarAdministrador =
    opcionesRol.includes("administrador") && infoAccesos?.esAdministrador;
  const mostrarAuditor =
    opcionesRol.includes("auditor") && infoAccesos?.esAuditor;
  const mostrarCliente = opcionesRol.includes("cliente") && esCliente;

  // Verificar si mostrar cambio de empresa
  const mostrarCambioEmpresa =
    permitirCambioEmpresa && infoAccesos?.tieneMultiplesClientes;

  // Verificar si hay alguna opción de rol para mostrar divisor
  const hayOpcionesRol =
    mostrarAdministrador ||
    mostrarAuditor ||
    mostrarCliente ||
    mostrarCambioEmpresa;

  // Contenido del menú (reutilizado en ambos modos)
  const menuContent = (
    <>
      <div>
        {/* Cambiar de empresa */}
        {mostrarCambioEmpresa && (
          <>
            <div
              css={{
                fontSize: "0.85rem",
                color: "var(--text-color-secondary)",
                marginBottom: "8px",
                paddingLeft: "12px"
              }}
            >
              Cambiar empresa
            </div>
            {infoAccesos.clientes.map((emp) => (
              <div
                key={emp.id}
                onClick={() =>
                  !cambiandoEmpresa &&
                  !emp.esActual &&
                  handleCambiarEmpresa(emp.id)
                }
                css={{
                  padding: "10px 12px",
                  cursor:
                    cambiandoEmpresa || emp.esActual ? "default" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  borderRadius: "4px",
                  backgroundColor: emp.esActual
                    ? "rgba(0, 0, 0, 0.04)"
                    : "transparent",
                  opacity: cambiandoEmpresa && !emp.esActual ? 0.5 : 1,
                  "&:hover": {
                    backgroundColor:
                      !cambiandoEmpresa && !emp.esActual
                        ? "rgba(0, 0, 0, 0.08)"
                        : undefined
                  }
                }}
              >
                <i
                  className="pi pi-building"
                  css={{ fontSize: "1rem", marginRight: "12px" }}
                />
                <span css={{ flex: 1 }}>{emp.razonsocial}</span>
                {emp.esActual && (
                  <i
                    className="pi pi-check"
                    css={{
                      fontSize: "0.9rem",
                      color: "var(--primary-color)"
                    }}
                  />
                )}
                {cambiandoEmpresa && !emp.esActual && (
                  <i
                    className="pi pi-spin pi-spinner"
                    css={{ fontSize: "0.9rem" }}
                  />
                )}
              </div>
            ))}
          </>
        )}

        {/* Divisor entre cambio de empresa y cambio de rol */}
        {mostrarCambioEmpresa &&
          (mostrarAdministrador || mostrarAuditor || mostrarCliente) && (
            <Divider css={{ margin: "12px 0px" }} />
          )}

        {/* Ir a Administrador */}
        {mostrarAdministrador && (
          <div
            onClick={handleIrAdministrador}
            css={{
              padding: "10px 12px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)"
              }
            }}
          >
            <i
              className="pi pi-shield"
              css={{ fontSize: "1rem", marginRight: "12px" }}
            />
            <span>Administrador</span>
          </div>
        )}

        {/* Ir a Auditor/Colaborador */}
        {mostrarAuditor && (
          <div
            onClick={handleIrAuditor}
            css={{
              padding: "10px 12px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              borderRadius: "4px",
              marginTop: mostrarAdministrador ? "4px" : "0px",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)"
              }
            }}
          >
            <i
              className="pi pi-clipboard"
              css={{ fontSize: "1rem", marginRight: "12px" }}
            />
            <span>Colaborador</span>
          </div>
        )}

        {/* Ir a Cliente */}
        {mostrarCliente && (
          <div
            onClick={handleIrCliente}
            css={{
              padding: "10px 12px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              borderRadius: "4px",
              marginTop: mostrarAdministrador || mostrarAuditor ? "4px" : "0px",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)"
              }
            }}
          >
            <i
              className="pi pi-briefcase"
              css={{ fontSize: "1rem", marginRight: "12px" }}
            />
            <span>Cliente</span>
          </div>
        )}

        {/* Divisor antes de las opciones custom/salir */}
        {hayOpcionesRol && <Divider css={{ margin: "12px 0px" }} />}

        {/* Botones custom */}
        {botonesCustom.map((boton, index) => (
          <div
            key={index}
            onClick={(e) => handleBotonCustom(boton.uri, e)}
            css={{
              padding: "10px 12px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              borderRadius: "4px",
              marginTop: index > 0 ? "4px" : "0px",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)"
              }
            }}
          >
            <i
              className={boton.icono || "pi pi-link"}
              css={{ fontSize: "1rem", marginRight: "12px" }}
            />
            <span>{boton.etiqueta}</span>
          </div>
        ))}

        {/* Salir */}
        <div
          onClick={handleLogout}
          css={{
            padding: "10px 12px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            borderRadius: "4px",
            marginTop: botonesCustom.length > 0 ? "4px" : "0px",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)"
            }
          }}
        >
          <i
            className="pi pi-sign-out"
            css={{ fontSize: "1rem", marginRight: "12px" }}
          />
          <span>Salir</span>
        </div>
      </div>

      {/* Info del usuario */}
      <Divider css={{ margin: "12px 0px" }} />
      <div
        css={{
          color: "var(--text-color-secondary)",
          fontSize: "0.85rem",
          paddingLeft: "12px"
        }}
      >
        {esUsuarioProvisorio
          ? "Usuario provisorio"
          : usuarioLogueado?.nombre && usuarioLogueado?.apellido
            ? `${usuarioLogueado.nombre} ${usuarioLogueado.apellido}`
            : usuarioLogueado?.email || "Usuario"}
      </div>
      <Version
        customCss={{
          color: "var(--text-color-secondary)",
          fontSize: "0.65rem",
          paddingLeft: "12px",
          marginTop: "4px",
          opacity: 0.7,
          fontWeight: "normal"
        }}
      />
    </>
  );

  // Modo expandido: renderiza directamente el contenido
  if (modoExpandido) {
    return (
      <>
        <Toast ref={toast} position="top-center" />
        <div>{menuContent}</div>
      </>
    );
  }

  // Modo normal: con OverlayPanel
  return (
    <>
      <Toast ref={toast} position="top-center" />
      <div
        css={{
          display: "flex",
          padding: "8px 12px",
          alignItems: "center",
          gap: "8px",
          cursor: "pointer"
        }}
        onClick={() => {
          setModalUsuarioAbierto((current) => !current);
        }}
        ref={botonPerfilUsuario}
      >
        {/* Avatar del usuario */}
        {mostrarAvatar && loader && <Skeleton shape="circle" size="2rem" />}
        {mostrarAvatar && !loader && usuarioLogueado && (
          <Avatar
            label={usuarioLogueado.nombre?.[0] || "U"}
            shape="circle"
            css={{
              backgroundColor: "rgba(239, 240, 252, 1)",
              color: "var(--principal)"
            }}
          />
        )}

        {/* Icono chevron */}
        <i
          className={`pi pi-chevron-down ${modalUsuarioAbierto ? "chevron-rotated" : ""}`}
          css={{
            fontSize: "16px",
            color: "white",
            transition: "transform 0.3s",
            "&.chevron-rotated": {
              transform: "rotate(180deg)"
            }
          }}
        />

        {/* Menú contextual */}
        <OverlayPanel
          ref={overlayMenuUsuario}
          onHide={() => setModalUsuarioAbierto(false)}
          css={{
            width: "260px"
          }}
        >
          {menuContent}
        </OverlayPanel>
      </div>
    </>
  );
}
